import { useElementSize } from '@byecode/ui'
import type { AppUser, UserCenterBlockAbstract, VariableADTvalue } from '@lighthouse/core'
import type { LoginAuthType, UserAvatarProps } from '@lighthouse/shared'
import {
    fieldConvertValue,
    flexMinSizeStyle,
    getBackgroundStyle,
    getCursorStyle,
    getShadowStyle,
    TagIcon,
    useApplicationContext,
    useFillPickerContext,
    useFlowLayoutContext,
    UserCenter
} from '@lighthouse/shared'
import React, { memo, useMemo } from 'react'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

interface UserCenterBlockProps {
    blockData: UserCenterBlockAbstract
    user?: AppUser
    onLogout?: () => void
    onLogin?: () => void
    onUpdateBindAccount?: (isBind: boolean, type: LoginAuthType) => Promise<boolean>
}

const SCxContainer = styled(AnimationDecorators)`
    padding: var(--block-padding);
    width: 100%;
    aspect-ratio: 1/1;
`

const getContentWidth = (el: HTMLDivElement | null) =>
    el ? el.clientWidth - Number.parseFloat(getComputedStyle(el).paddingLeft) - Number.parseFloat(getComputedStyle(el).paddingRight) : 0

const UserCenterBlock: React.FunctionComponent<UserCenterBlockProps> = ({ blockData, user, onLogout, onLogin }) => {
    const {
        id,
        title,
        config: {
            showUserSetting,
            breakPoint: { design, size, animation },
            cursor,
            anchor
        }
    } = blockData
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const { shadow, color, border, background, radius } = design ?? {}

    const { appId = '', isMobile } = useApplicationContext()
    const { palettes } = useFillPickerContext()
    const { useParseVariableWithSubscription } = useFlowLayoutContext()

    const parseBackgroundColorVariable = useParseVariableWithSubscription(background?.variable)

    const { ref, width: containerWidth } = useElementSize<HTMLDivElement>()

    const avatarProps: Partial<UserAvatarProps> = useMemo(() => {
        const size = Math.max(0, (containerWidth || getContentWidth(ref.current)) - ((border?.each?.[1] ?? 0) + (border?.each?.[3] ?? 0)))

        const borderWidth = border?.each ?? [0, 0, 0, 0]
        const borderRadius = radius ?? [0, 0, 0, 0]
        return {
            color: color?.color,
            // background: background?.color,
            size,
            fontSize: user?.userId ? size * 0.5 : size * 0.7,
            style: {
                ...getBackgroundStyle({
                    value: background,
                    palettes,
                    parseVariableColor: (v: VariableADTvalue) => {
                        const field = parseBackgroundColorVariable(v)
                        return field ? fieldConvertValue(field) : ''
                    }
                }),
                ...getShadowStyle(shadow, palettes),
                borderWidth: `${borderWidth[1]}px ${borderWidth[2]}px ${borderWidth[3]}px ${borderWidth[0]}px`,
                borderRadius: `${borderRadius[0]}px ${borderRadius[1]}px ${borderRadius[3]}px ${borderRadius[2]}px`,
                borderColor: border?.color,
                borderStyle: 'solid',
                boxSizing: 'content-box'
            }
        }
    }, [
        background,
        border?.color,
        border?.each,
        color?.color,
        containerWidth,
        palettes,
        parseBackgroundColorVariable,
        radius,
        ref,
        shadow,
        user?.userId
    ])

    return (
        <SCxContainer
            ref={ref}
            blockId={id}
            blockName={title}
            animation={animation}
            id={effectiveAnchorId}
            style={{ ...flexMinSizeStyle, ...getCursorStyle(appId, cursor) }}
        >
            {user?.userId ? (
                <UserCenter
                    userData={user}
                    isMobile={isMobile}
                    avatarProps={avatarProps}
                    hideUserSetting={!showUserSetting}
                    onLogout={onLogout}
                />
            ) : (
                <TagIcon
                    icon="User"
                    size={typeof avatarProps.size === 'number' ? avatarProps.size : 32}
                    iconColor={avatarProps.color}
                    iconSize={avatarProps.fontSize}
                    background={avatarProps.background}
                    style={avatarProps.style}
                    onClick={onLogin}
                />
            )}

            {/* <IconRender value={content} /> */}
        </SCxContainer>
    )
}

export default memo(UserCenterBlock)
